import React, { useRef, useState } from "react";
import { post } from "../../services/ApiService";

const FileUpload = ({
  name,
  value = null,
  onChange,
  accept = "image/*",
  className = "",
}) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (optional - 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Upload the file
      const filename = await onUpload(file);
      onChange(filename);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    setIsUploading(true);
    try {
      await onRemove(value);
      onChange("");
    } catch (error) {
      console.error("Error removing file:", error);
      alert("Failed to remove file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleContainerClick = () => {
    if (!value && !isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onUpload = async (file) => {
    const formDataObj = new FormData();
    formDataObj.append("file", file);
    
    try {
      // Simulate progress (you can replace this with actual progress events if your API supports it)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const res = await post("api/home/file-upload", formDataObj, true);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Small delay to show 100% completion
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (res.success) {
        return res.file;
      } else {
        throw new Error(res.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const onRemove = async (file) => {
    try {
      const res = await post("api/home/file-remove", { filename: file });
      return res;
    } catch (error) {
      console.error("Error in file removal:", error);
      throw error;
    }
  };

  return (
    <div className={`file-upload-container ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={accept}
        style={{ display: "none" }}
        disabled={isUploading}
      />

      {value ? (
        <div className="file-preview position-relative">
          <div className="image-container text-center">
            <img
              src={`${process.env.REACT_APP_API_ENDPOINT}/uploads/${value}`}
              alt="Uploaded preview"
              className="img-thumbnail uploaded-image"
              style={{ 
                maxHeight: "200px", 
                maxWidth: "100%",
                opacity: isUploading ? 0.6 : 1 
              }}
            />
            {!isUploading && (
              <button
                type="button"
                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                onClick={handleRemove}
                style={{ zIndex: 1 }}
                title="Remove image"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
          {isUploading && (
            <div className="position-absolute top-50 start-50 translate-middle">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Removing...</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`file-upload-area border rounded p-4 text-center position-relative ${
            isUploading ? "uploading" : "cursor-pointer"
          }`}
          onClick={handleContainerClick}
          style={{
            borderStyle: "dashed",
            backgroundColor: isUploading ? "#f0f8ff" : "#f8f9fa",
            borderColor: isUploading ? "#0d6efd" : "#dee2e6",
            transition: "all 0.3s ease",
            minHeight: "180px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {isUploading ? (
            <div className="upload-progress-content w-100">
              {/* Animated spinner */}
              <div className="mb-3">
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Uploading...</span>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="progress mb-2" style={{ height: "8px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              
              {/* Progress text */}
              <div className="upload-status">
                <p className="mb-1 fw-semibold text-primary">Uploading...</p>
                <p className="small text-muted mb-0">
                  {uploadProgress}% complete
                </p>
              </div>
            </div>
          ) : (
            <div className="upload-ready-content">
              <i className="bi bi-cloud-arrow-up fs-1 text-muted mb-2"></i>
              <p className="mb-1 fw-semibold">Click to upload image</p>
              <p className="small text-muted mb-0">
                Supported formats: JPG, PNG, GIF
              </p>
              <p className="small text-muted mt-1">
                Maximum file size: 5MB
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;